import type { TemplateContext } from "@unbox/api";
import { useState } from "react";
import { useTemplateContext } from "@/api/assurance/actions";
import type { ComposerImage } from "@/components/message-composer/composer-image";
import { MessageComposerField } from "@/components/message-composer/message-composer-field";
import { type Creator, CreatorTagsList } from "@/components/message-creator-dialogs";
import { formatCreatorIdShort } from "@/utils/formatters";

const SINGLE_VIDEO_TOOLTIP =
  "This video does not have this information, as it was generated before this explanation was added to our system. Therefore, we've fallen back to the risk summary we surface to you on the video. This is just slightly different wording and is not tailored as such to the creator. Select preview to see what this will output.";
const MULTI_VIDEO_TOOLTIP =
  "Not all videos have creator-facing explanations. For those that don't, we'll use the risk summary we surface to you on the video instead. This is just slightly different wording and is not tailored as such to the creator. This is only temporary—going forward, all videos will have creator-facing explanations.";

type MessageCreatorFormFieldsProps = {
  form: any;
  disabled?: boolean;
  videoCount: number;
  creators?: Creator[];
  videoIds: string[];
  image: ComposerImage | null;
  onImageChange: (image: ComposerImage | null) => void;
  onPreviewChange?: (showPreview: boolean) => void;
};

export const MessageCreatorFormFields = ({
  form,
  disabled,
  videoCount,
  creators,
  videoIds,
  image,
  onImageChange,
  onPreviewChange: onPreviewChangeExternal,
}: MessageCreatorFormFieldsProps) => {
  const isBulk = videoCount > 1;
  const [previewCreatorId, setPreviewCreatorId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handlePreviewChange = (value: boolean) => {
    setShowPreview(value);
    onPreviewChangeExternal?.(value);
  };

  const { data: templateContext } = useTemplateContext(videoIds.length > 0 ? { mode: "include", videoIds } : null);

  const previewCreator = creators?.find((c) => c.creatorId === previewCreatorId) ?? creators?.[0];

  const creatorData = (() => {
    if (!previewCreator || !templateContext) return undefined;
    return templateContext.creators.find((c) => c.creatorId === previewCreator.creatorId);
  })();

  const unavailableVariables: Record<string, string> | undefined = (() => {
    if (!creatorData || !templateContext) return undefined;
    const hasAnyMissing = isBulk
      ? templateContext.creators.some((c) => c.creatorRiskExplanations.length === 0)
      : creatorData.creatorRiskExplanations.length === 0;
    if (!hasAnyMissing) return undefined;
    return { "{creator_risk_explanation}": isBulk ? MULTI_VIDEO_TOOLTIP : SINGLE_VIDEO_TOOLTIP };
  })();

  const previewContext: TemplateContext | undefined = (() => {
    if (!previewCreator || !creatorData || !templateContext) return undefined;
    const fallbackRiskExplanations =
      creatorData.flagExplanations.length > 0 ? [creatorData.flagExplanations.join("\n")] : undefined;
    return {
      creatorName: previewCreator.handle ?? formatCreatorIdShort(previewCreator.creatorId),
      shopName: templateContext.shopName,
      videoUrls: creatorData.videoUrls.length > 0 ? creatorData.videoUrls : undefined,
      flagTypes: creatorData.flagTypes.length > 0 ? creatorData.flagTypes : undefined,
      flagExplanations: creatorData.flagExplanations.length > 0 ? creatorData.flagExplanations : undefined,
      productNameList: creatorData.productNameList.length > 0 ? creatorData.productNameList : undefined,
      creatorRiskExplanations:
        creatorData.creatorRiskExplanations.length > 0 ? creatorData.creatorRiskExplanations : fallbackRiskExplanations,
    };
  })();

  return (
    <>
      {creators && creators.length > 0 && (
        <CreatorTagsList
          creators={creators}
          isBulk={isBulk}
          isPending={disabled ?? false}
          onCreatorClick={isBulk ? setPreviewCreatorId : undefined}
          activeCreatorId={showPreview ? previewCreator?.creatorId : undefined}
          forceExpanded={showPreview}
        />
      )}

      <MessageComposerField
        control={form.control}
        name="message"
        image={image}
        onImageChange={onImageChange}
        disabled={disabled}
        variableContext="safety"
        previewContext={previewContext}
        unavailableVariables={unavailableVariables}
        onPreviewChange={handlePreviewChange}
        autoFocus
      />
    </>
  );
};
